import { type FunctionComponent, startTransition, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentReadMoreContext } from "scmp-app/components/content/content-read-more/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { contentReadMoreButtonContent$key } from "scmp-app/queries/__generated__/contentReadMoreButtonContent.graphql";

import { AppButton } from "./app-button";
import IconLoading from "./icon-loading.gif";
import { Container, ReadFullArticleButton, StyledBaseImage } from "./styles";

export type Props = {
  className?: string;
  reference: contentReadMoreButtonContent$key;
};

export const ContentReadMoreButton: FunctionComponent<Props> = ({ reference, ...attribs }) => {
  const content = useFragment(
    graphql`
      fragment contentReadMoreButtonContent on Content {
        ...appButtonContent
      }
    `,
    reference,
  );

  const [isReadMoreLoading, setIsReadMoreLoading] = useState(false);
  const { isReadMoreClicked, setIsReadMoreClicked } = useContentReadMoreContext();
  if (isReadMoreClicked) return null;

  return (
    <Container {...attribs}>
      <ReadFullArticleButton
        noStyle
        onClick={() => {
          setIsReadMoreLoading(true);
          startTransition(() => {
            setIsReadMoreClicked?.(true);
            setIsReadMoreLoading(false);
            sendGA4Tracking({
              action: "click",
              category: "expand",
              customized_parameters: {
                action_type: "read_full_article",
              },
              subcategory: "",
            });
          });
        }}
      >
        {isReadMoreLoading ? (
          <>
            Loading...
            <StyledBaseImage
              $aspectRatio="1"
              $height="24px"
              $objectFit="contain"
              $width="24px"
              alt={IconLoading.alt}
              aria-label="loading"
              disableLinkingData
              lazyload={false}
              src={IconLoading.src}
            />
          </>
        ) : (
          "READ FULL ARTICLE"
        )}
      </ReadFullArticleButton>
      <AppButton reference={content} />
    </Container>
  );
};

ContentReadMoreButton.displayName = "ContentReadMoreButton";
